import React, {
  useEffect,
  useContext,
  useState
} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Doughnut } from 'react-chartjs-2';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  colors,
  makeStyles,
  useTheme,
} from '@material-ui/core';

import { useHttpClient } from '../../../../hooks/http-hook';
import { AuthContext } from '../../../../shared/context/auth-context';
import LoadingSpinner from '../../../../shared/component/UIElements/LoadingSpinner';
import ErrorMessage from '../../../../shared/component/UIElements/ErrorMessage';

const useStyles = makeStyles(() => ({
  root: {
    height: '100%'
  },
  header: {
    border: '1px solid #ACACAC',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    '& .MuiCardHeader-title': {
      fontSize: '20px',
      fontWeight: 700,
      fontFamily: 'Roboto',
      color: '#3E3E3E'
    }
  },
  container: {
    borderLeft: '1px solid #ACACAC',
    borderRight: '1px solid #ACACAC',
    borderBottom: '1px solid #ACACAC',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    padding: '0px 0px 0px 0px',
    '&:last-child': {
      paddingBottom: 0
    }
  },
  box: {
    flexGrow: 1
  },
  borda: {
    borderRight: '1px solid #ACACAC'
  },
  bordat: {
    borderTop: '1px solid #ACACAC',
    marginTop: '0px'
  },
  numero: {
    display: 'block',
    width: '100%',
    fontSize: '43px',
    fontWeight: 500,
    color: '#3E3E3E',
    textAlign: 'center'
  },
  descricao: {
    fontSize: '21px',
    fontWeight: 700,
    color: '#808080',
    textAlign: 'center',
    // maxWidth: '166px',
    lineHeight: '21px',
    display: 'block',
    width: '100%',
  },
}));

const TrafficByDevice = ({ className, ...rest }) => {
  const classes = useStyles();
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const [dashboard, setDashboard] = useState({
    data: {
      datasets: [
        {
          data: [63, 15, 22],
          backgroundColor: [
            '#C03A2A',
            '#51B36E',
            '#808080'
          ],
          borderWidth: 8,
          borderColor: colors.common.white,
          hoverBorderColor: colors.common.white
        }
      ],
      labels: ['Ruim', 'Bom', 'Regular']
    },
    devices: [
      {
        title: 'Ruim',
        value: 63,
        color: '#C03A2A'
      },
      {
        title: 'Bom',
        value: 15,
        color: '#51B36E'
      },
      {
        title: 'Regular',
        value: 23,
        color: '#808080'
      }
    ]
  });
  const {
    isLoading,
    error,
    sendRequest,
    clearError
  } = useHttpClient();
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/relatorios/estado`,
          'GET',
          null,
          {
            'Content-Type': 'application/json',
            'x-access-token': auth.token
          }
        );
        console.log(responseData);
        if (responseData.sucesso) {
          setDashboard((prevState) => {
            const data = prevState;
            const total = responseData.data.total;
            const ruim = responseData.data.ruim;
            const bom = responseData.data.bom;
            const regular = responseData.data.regular;
            const porcentagembom = parseFloat((bom * 100) / total);
            const porcentagemruim = parseFloat((ruim * 100) / total);
            const porcentagemregular = parseFloat((regular * 100) / total);
            data.data.datasets[0].data = [porcentagemruim, porcentagembom, porcentagemregular];
            data.devices[0].value = ruim;
            data.devices[1].value = bom;
            data.devices[2].value = regular;
            return data;
          });
          clearError();
        }
      } catch (err) {
        console.log(err);
      }
    };
    fetchDashboard();
  }, [sendRequest, auth]);

  const options = {
    animation: false,
    cutoutPercentage: 80,
    layout: { padding: 0 },
    legend: {
      display: false
    },
    maintainAspectRatio: false,
    responsive: true,
    tooltips: {
      backgroundColor: theme.palette.background.default,
      bodyFontColor: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      enabled: true,
      footerFontColor: theme.palette.text.secondary,
      intersect: false,
      mode: 'index',
      titleFontColor: theme.palette.text.primary
    }
  };

  if (isLoading) {
    return (
      <>
        <LoadingSpinner asOverlay />
      </>
    );
  }

  return (
    <Card
      className={clsx(classes.root, className)}
      {...rest}
    >
      <CardHeader
        title="Estado geral de conservação do acervo"
        className={classes.header}
      />
      <Divider />
      <CardContent
        className={classes.container}
      >
        <Box
          height={300}
          position="relative"
          style={{ padding: 10 }}
        >
          <Doughnut
            data={dashboard.data}
            options={options}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          mt={2}
          className={classes.bordat}
        >
          {dashboard.devices.map(({
            color,
            title,
            value
          }, index) => (
            <Box
              key={title}
              p={1}
              textAlign="center"
              className={clsx(classes.box, index < dashboard.devices.length - 1 ? classes.borda : '')}
            >
              <Typography
                variant="h2"
                className={clsx(classes.numero)}
              >
                {value}
              </Typography>
              <Typography
                color="textPrimary"
                className={clsx(classes.descricao)}
                style={{ color }}
                variant="body1"
              >
                {title}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
      <ErrorMessage error={error} onClear={clearError} />
    </Card>
  );
};

TrafficByDevice.propTypes = {
  className: PropTypes.string
};

export default TrafficByDevice;
